import { useState, useEffect } from "react";
import api from "../../lib/api";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Textarea } from "../../components/ui/textarea";
import { Label } from "../../components/ui/label";
import { Download, Loader2, Briefcase, FileText, Pencil, MessageSquare, Sparkles, Check, ArrowRight, ArrowLeft, Users, Search } from "lucide-react";
import { motion } from "framer-motion";

const DEFAULT_CONTENT = {
  company_name: "Stansbury AI",
  prospect_name: "",
  prospect_company: "",
  prospect_industry: "",
  tagline: "Turn AI Into Your #1 Revenue Engine",
  subtitle: "We map your operational infrastructure, quantify revenue leaks, and architect the AI systems that eliminate them.",
  pain_points: [
    "Leads slip through the cracks because follow-up is manual and inconsistent",
    "Your team spends 30-50% of their time on tasks AI could handle in seconds",
    "Growth is unpredictable — some months are full, others are empty",
    "You know AI matters, but you don't know where to start or what to prioritize",
  ],
  solution_intro: "The Stansbury AI Transformation Blueprint is a comprehensive operational assessment across 12 business dimensions that quantifies revenue leaks, maps AI transformation opportunities, and delivers an executive-grade implementation roadmap with ROI projections.",
  dimensions: [
    { name: "Sales Process", desc: "Lead qualification, pipeline visibility, follow-up consistency, and speed-to-lead" },
    { name: "Marketing", desc: "Channel strategy, content production, personalization, and attribution tracking" },
    { name: "Customer Experience", desc: "Onboarding, retention, feedback loops, and upsell/cross-sell systems" },
    { name: "Operations", desc: "Process automation, reporting maturity, documentation, and bottleneck elimination" },
    { name: "Technology Stack", desc: "Tool integration, CRM maturity, data quality, and automation readiness" },
    { name: "AI Readiness", desc: "Leadership buy-in, budget allocation, change readiness, and implementation timeline" },
  ],
  deliverables: [
    "AI Readiness Score across 12 operational dimensions",
    "Revenue Leak Analysis with dollar estimates",
    "AI Opportunity Matrix ranked by ROI",
    "Department-level transformation plans",
    "Implementation roadmap with phased execution",
    "ROI modeling with payback projections",
    "System architecture diagrams",
    "Downloadable executive PDF report",
  ],
  results_stats: [
    { stat: "20-35%", desc: "increase in lead conversion rates" },
    { stat: "15-30%", desc: "revenue lift within 90-120 days" },
    { stat: "20-40 hrs", desc: "per week freed from manual tasks" },
    { stat: "50-70%", desc: "faster sales cycles" },
  ],
  case_study_company: "Quantus Investment Corp",
  case_study_industry: "Exempt Market Dealer",
  case_study_challenge: "Pipeline volatility, manual investor outreach, inconsistent follow-ups, and advisor-dependent growth that couldn't scale.",
  case_study_solution: "Installed an Agentic AI infrastructure to replace manual investor acquisition with autonomous, always-on outreach and conversion systems.",
  case_study_results: [
    "+34% revenue growth in 120 days",
    "2.3x more qualified investor meetings",
    "~40% faster sales cycles",
    "Zero additional headcount required",
  ],
  case_study_quote: "The AI infrastructure didn't just improve performance. It replaced the growth model entirely.",
  pricing: "Contact Us",
  cta_text: "Ready to see where AI can drive revenue for your business?",
  cta_action: "Book a Strategy Call",
  contact_url: "https://stansbury.ai/booking",
};

export default function OpsSalesMaterial() {
  const [step, setStep] = useState(1); // 1=prospect, 2=tailor+edit, 3=preview+download
  const [content, setContent] = useState(DEFAULT_CONTENT);
  const [leads, setLeads] = useState([]);
  const [searchQuery, setSearchQuery] = useState("");
  const [selectedLead, setSelectedLead] = useState(null);
  const [downloading, setDownloading] = useState(false);
  const [chatPrompt, setChatPrompt] = useState("");
  const [tailoring, setTailoring] = useState(false);
  const [tailored, setTailored] = useState(false);

  useEffect(() => {
    const loadLeads = async () => {
      try {
        const { data } = await api.get("/ops/leads");
        setLeads(data);
      } catch (e) { console.error("Leads load failed:", e); }
    };
    loadLeads();
  }, []);

  const updateField = (key, value) => setContent(prev => ({ ...prev, [key]: value }));
  const updateArrayItem = (key, index, value) => {
    const arr = [...content[key]];
    arr[index] = value;
    setContent(prev => ({ ...prev, [key]: arr }));
  };

  const selectLead = (lead) => {
    setSelectedLead(lead);
    setContent(prev => ({
      ...prev,
      prospect_name: lead.name || "",
      prospect_company: lead.company || "",
      prospect_industry: (lead.industry || "").replace(/_/g, " "),
    }));
  };

  const handleTailor = async () => {
    if (!chatPrompt.trim() && !content.prospect_company) return;
    setTailoring(true);
    try {
      const { data } = await api.post("/ops/sales-deck/tailor", {
        prompt: chatPrompt || `Tailor for ${content.prospect_company}`,
        prospect_name: content.prospect_name,
        prospect_company: content.prospect_company,
        prospect_industry: content.prospect_industry,
      });
      setContent(prev => ({
        ...prev,
        tagline: data.tagline || prev.tagline,
        subtitle: data.subtitle || prev.subtitle,
        pain_points: Array.isArray(data.pain_points) && data.pain_points.length >= 3 ? data.pain_points : prev.pain_points,
        solution_intro: data.solution_intro || prev.solution_intro,
        case_study_challenge: data.case_study_challenge || prev.case_study_challenge,
        cta_text: data.cta_text || prev.cta_text,
      }));
      setTailored(true);
      setChatPrompt("");
    } catch (err) {
      console.error("Tailor failed:", err);
    } finally {
      setTailoring(false);
    }
  };

  const handleDownload = async () => {
    setDownloading(true);
    try {
      const response = await api.post("/ops/sales-deck", content, { responseType: "blob" });
      const url = window.URL.createObjectURL(new Blob([response.data], { type: "application/pdf" }));
      const link = document.createElement("a");
      link.href = url;
      const suffix = content.prospect_company ? `_${content.prospect_company.replace(/\s/g, "_")}` : "";
      link.setAttribute("download", `Stansbury_AI_Sales_Deck${suffix}.pdf`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (err) { console.error("Sales deck download failed:", err); }
    finally { setDownloading(false); }
  };

  const filteredLeads = leads.filter(l => {
    if (!searchQuery) return true;
    const q = searchQuery.toLowerCase();
    return (l.name || "").toLowerCase().includes(q) || (l.company || "").toLowerCase().includes(q) || (l.email || "").toLowerCase().includes(q);
  });

  return (
    <div data-testid="ops-sales-material">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-2xl font-['Outfit'] font-medium text-[#0F172A]">Sales Material</h1>
          <p className="text-sm text-slate-500 font-['IBM_Plex_Sans'] mt-1">Generate targeted sales decks for prospects.</p>
        </div>
        {/* Step indicator */}
        <div className="flex items-center gap-2">
          {[{ n: 1, label: "Prospect" }, { n: 2, label: "Customize" }, { n: 3, label: "Download" }].map(({ n, label }) => (
            <button key={n} onClick={() => n <= step && setStep(n)} className={`flex items-center gap-1.5 px-3 py-1.5 text-xs font-['Outfit'] transition-colors ${step === n ? "bg-[#0033CC] text-white" : step > n ? "bg-emerald-50 text-emerald-700" : "bg-slate-100 text-slate-400"}`} data-testid={`step-${n}`}>
              {step > n ? <Check className="w-3 h-3" /> : <span className="w-4 text-center">{n}</span>}
              {label}
            </button>
          ))}
        </div>
      </div>

      {/* STEP 1: Select Prospect */}
      {step === 1 && (
        <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} className="space-y-4" data-testid="step-prospect">
          {/* Manual entry */}
          <div className="bg-white border border-slate-200 p-6">
            <h3 className="text-sm font-['Outfit'] font-medium text-[#0F172A] mb-4">Who is this deck for?</h3>
            <div className="grid grid-cols-3 gap-3 mb-4">
              <div><Label className="text-xs text-slate-400 mb-1 block">Contact Name</Label><Input value={content.prospect_name} onChange={e => updateField("prospect_name", e.target.value)} placeholder="e.g. Sarah Johnson" className="rounded-none border-slate-300 h-9 text-sm" data-testid="prospect-name-input" /></div>
              <div><Label className="text-xs text-slate-400 mb-1 block">Company</Label><Input value={content.prospect_company} onChange={e => updateField("prospect_company", e.target.value)} placeholder="e.g. Acme Corp" className="rounded-none border-slate-300 h-9 text-sm" data-testid="prospect-company-input" /></div>
              <div><Label className="text-xs text-slate-400 mb-1 block">Industry</Label><Input value={content.prospect_industry} onChange={e => updateField("prospect_industry", e.target.value)} placeholder="e.g. Financial Services" className="rounded-none border-slate-300 h-9 text-sm" data-testid="prospect-industry-input" /></div>
            </div>
          </div>

          {/* Or select from leads */}
          <div className="bg-white border border-slate-200 p-6">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-sm font-['Outfit'] font-medium text-[#0F172A]">Or select from your leads</h3>
              <div className="relative w-64">
                <Search className="w-3.5 h-3.5 absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
                <Input value={searchQuery} onChange={e => setSearchQuery(e.target.value)} placeholder="Search leads..." className="rounded-none border-slate-300 h-8 text-xs pl-9" data-testid="lead-search-input" />
              </div>
            </div>
            <div className="space-y-1.5 max-h-[280px] overflow-y-auto">
              {filteredLeads.slice(0, 10).map((l, i) => (
                <button key={l.id} onClick={() => selectLead(l)}
                  className={`w-full text-left flex items-center gap-3 p-3 border transition-colors ${selectedLead?.id === l.id ? "border-[#0033CC] bg-[#0033CC]/[0.03]" : "border-slate-100 hover:bg-slate-50"}`}
                  data-testid={`lead-option-${i}`}>
                  <div className="w-8 h-8 bg-slate-100 flex items-center justify-center text-xs font-['JetBrains_Mono'] text-[#0033CC] uppercase">{(l.name || "?")[0]}</div>
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-['Outfit'] font-medium text-[#0F172A] truncate">{l.name || l.email}</p>
                    <p className="text-xs text-slate-400 font-['IBM_Plex_Sans']">{l.company} {l.industry ? `\u00b7 ${l.industry.replace(/_/g, " ")}` : ""}</p>
                  </div>
                  {selectedLead?.id === l.id && <Check className="w-4 h-4 text-[#0033CC]" />}
                </button>
              ))}
              {filteredLeads.length === 0 && <p className="text-xs text-slate-400 text-center py-4">No leads found</p>}
            </div>
          </div>

          <div className="flex justify-end">
            <Button onClick={() => setStep(2)} className="bg-[#0033CC] text-white hover:bg-[#002299] rounded-none h-10 px-6 font-['Outfit'] text-sm" data-testid="next-to-customize">
              {content.prospect_company ? `Customize for ${content.prospect_company}` : "Continue to Customize"} <ArrowRight className="w-4 h-4 ml-2" />
            </Button>
          </div>
        </motion.div>
      )}

      {/* STEP 2: AI Tailor + Edit */}
      {step === 2 && (
        <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} className="space-y-4" data-testid="step-customize">
          {/* AI Tailor */}
          <div className="bg-[#0F172A] p-5" data-testid="sales-tailor-section">
            <div className="flex items-center gap-2 mb-3">
              <MessageSquare className="w-4 h-4 text-[#818CF8]" />
              <span className="text-xs font-['JetBrains_Mono'] uppercase tracking-[0.15em] text-[#818CF8]">AI Tailor</span>
              {content.prospect_company && <span className="text-xs text-slate-500 font-['IBM_Plex_Sans']">for {content.prospect_company}</span>}
            </div>
            <p className="text-xs text-slate-400 font-['IBM_Plex_Sans'] mb-3">
              Describe the prospect's situation and the AI will customize the deck — pain points, solution framing, and CTA.
            </p>
            <Textarea
              value={chatPrompt}
              onChange={e => setChatPrompt(e.target.value)}
              placeholder={content.prospect_company
                ? `e.g. "${content.prospect_company} is struggling with manual lead follow-up. They're losing deals to faster competitors. Focus on speed and automation."`
                : `e.g. "This is a mid-market SaaS company losing deals because their sales team can't keep up with inbound leads..."`}
              className="bg-white/5 border-white/10 text-white placeholder:text-slate-500 rounded-none text-sm min-h-[70px] resize-none mb-3"
              data-testid="sales-tailor-input"
              onKeyDown={e => { if (e.key === "Enter" && !e.shiftKey) { e.preventDefault(); handleTailor(); } }}
            />
            <div className="flex items-center justify-between">
              {tailored && <span className="flex items-center gap-1.5 text-xs text-emerald-400"><Check className="w-3 h-3" /> Deck tailored</span>}
              {!tailored && <span />}
              <Button onClick={handleTailor} disabled={tailoring || (!chatPrompt.trim() && !content.prospect_company)} className="bg-[#818CF8] text-white hover:bg-[#6366F1] rounded-none h-8 px-4 text-xs font-['Outfit']" data-testid="sales-tailor-btn">
                {tailoring ? <Loader2 className="w-3.5 h-3.5 animate-spin mr-1.5" /> : <Sparkles className="w-3.5 h-3.5 mr-1.5" />}
                {tailoring ? "Tailoring..." : "Tailor with AI"}
              </Button>
            </div>
          </div>

          {/* Editable content */}
          <div className="space-y-4">
            <Section title="Cover Slide">
              <Field label="Tagline" value={content.tagline} onChange={v => updateField("tagline", v)} />
              <Field label="Subtitle" value={content.subtitle} onChange={v => updateField("subtitle", v)} textarea />
            </Section>
            <Section title="Pain Points (Slide 2)">
              {content.pain_points.map((p, i) => (
                <Field key={`pp-${i}`} label={`Pain Point ${i + 1}`} value={p} onChange={v => updateArrayItem("pain_points", i, v)} />
              ))}
            </Section>
            <Section title="Solution (Slide 3)">
              <Field label="Solution Intro" value={content.solution_intro} onChange={v => updateField("solution_intro", v)} textarea />
            </Section>
            <Section title="Client Results (Slide 5)">
              {content.results_stats.map((r, i) => (
                <div key={`rs-${i}`} className="grid grid-cols-3 gap-2">
                  <Field label="Stat" value={r.stat} onChange={v => updateArrayItem("results_stats", i, { ...r, stat: v })} />
                  <div className="col-span-2"><Field label="Description" value={r.desc} onChange={v => updateArrayItem("results_stats", i, { ...r, desc: v })} /></div>
                </div>
              ))}
            </Section>
            <Section title="Pricing & CTA (Slide 8)">
              <Field label="Pricing" value={content.pricing} onChange={v => updateField("pricing", v)} />
              <Field label="CTA Text" value={content.cta_text} onChange={v => updateField("cta_text", v)} />
            </Section>
          </div>

          <div className="flex justify-between">
            <Button onClick={() => setStep(1)} variant="outline" className="rounded-none h-10 px-5 font-['Outfit'] text-sm" data-testid="back-to-prospect">
              <ArrowLeft className="w-4 h-4 mr-2" />Back
            </Button>
            <Button onClick={() => setStep(3)} className="bg-[#0033CC] text-white hover:bg-[#002299] rounded-none h-10 px-6 font-['Outfit'] text-sm" data-testid="next-to-preview">
              Preview & Download <ArrowRight className="w-4 h-4 ml-2" />
            </Button>
          </div>
        </motion.div>
      )}

      {/* STEP 3: Preview + Download */}
      {step === 3 && (
        <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} className="space-y-4" data-testid="step-download">
          {content.prospect_company && (
            <div className="bg-emerald-50 border border-emerald-200 p-4 flex items-center gap-3">
              <Users className="w-4 h-4 text-emerald-600" />
              <div>
                <p className="text-sm font-['Outfit'] font-medium text-[#0F172A]">Tailored for {content.prospect_company}</p>
                <p className="text-xs text-slate-500 font-['IBM_Plex_Sans']">{content.prospect_name} {content.prospect_industry ? `\u00b7 ${content.prospect_industry}` : ""}</p>
              </div>
            </div>
          )}

          {/* Slide previews */}
          <div className="space-y-2">
            {[
              { num: "01", title: "Cover", desc: content.tagline, color: "bg-[#0F172A] text-white" },
              { num: "02", title: "The Problem", desc: `${content.pain_points.length} pain points targeting prospect challenges`, color: "bg-[#0F172A] text-white" },
              { num: "03", title: "The Solution", desc: "Stansbury AI Transformation Blueprint — 12 dimensions, AI infrastructure, agentic workflows", color: "bg-white" },
              { num: "04", title: "How It Works", desc: "Perceive > Decide > Act > Learn — Agentic AI framework", color: "bg-white" },
              { num: "05", title: "Client Results", desc: content.results_stats.map(r => r.stat).join(" \u00b7 "), color: "bg-white" },
              { num: "06", title: "Case Study", desc: "+34% revenue growth, 2.3x meetings, ~40% faster cycles", color: "bg-[#0F172A] text-white" },
              { num: "07", title: "Case Study Results", desc: content.case_study_quote.substring(0, 80) + "...", color: "bg-white" },
              { num: "08", title: "Pricing & CTA", desc: `${content.pricing}`, color: "bg-[#0033CC] text-white" },
            ].map((slide, i) => (
              <motion.div key={slide.num} initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: i * 0.03 }}
                className={`${slide.color} border border-slate-200 p-4 flex items-center gap-4`}>
                <span className="text-xs font-['JetBrains_Mono'] opacity-50 w-6">{slide.num}</span>
                <div className="flex-1">
                  <p className="text-sm font-['Outfit'] font-medium">{slide.title}</p>
                  <p className="text-xs opacity-60 font-['IBM_Plex_Sans'] mt-0.5">{slide.desc}</p>
                </div>
                <FileText className="w-4 h-4 opacity-30" />
              </motion.div>
            ))}
          </div>

          <div className="flex justify-between">
            <Button onClick={() => setStep(2)} variant="outline" className="rounded-none h-10 px-5 font-['Outfit'] text-sm" data-testid="back-to-customize">
              <ArrowLeft className="w-4 h-4 mr-2" />Edit Content
            </Button>
            <Button onClick={handleDownload} disabled={downloading} className="bg-[#0033CC] text-white hover:bg-[#002299] rounded-none h-10 px-6 font-['Outfit'] text-sm" data-testid="download-sales-deck">
              {downloading ? <Loader2 className="w-4 h-4 animate-spin mr-1.5" /> : <Download className="w-4 h-4 mr-1.5" />}
              {downloading ? "Generating..." : "Download Sales Deck"}
            </Button>
          </div>
        </motion.div>
      )}
    </div>
  );
}

function Section({ title, children }) {
  return (
    <div className="bg-white border border-slate-200 p-5">
      <h3 className="text-sm font-['Outfit'] font-medium text-[#0F172A] mb-3">{title}</h3>
      <div className="space-y-3">{children}</div>
    </div>
  );
}

function Field({ label, value, onChange, textarea }) {
  return (
    <div>
      <Label className="text-xs text-slate-400 mb-1 block">{label}</Label>
      {textarea ? (
        <Textarea value={value} onChange={e => onChange(e.target.value)} className="rounded-none border-slate-300 text-sm min-h-[60px]" />
      ) : (
        <Input value={value} onChange={e => onChange(e.target.value)} className="rounded-none border-slate-300 h-8 text-sm" />
      )}
    </div>
  );
}
